/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 */


import {STATUS_MODEL} from "../../../../../enums/statusEnum";
import {USER_TYPE_MODEL} from "../../../../../enums/userTypeEnum";
import {permissionObj} from "../../../../../Helpers/permission";

export const StaticMsg = {
    PENDING_TO_UPLOAD_INFORMATION: {
        id: STATUS_MODEL.PENDING_TO_UPLOAD_INFORMATION,
        tittle : 'pending_review',
        toast: "pending_to_upload_information",
        classCard: "card-shades-150-50",
        fontColor: "font-color-shades-700",
        infoColor: "icon-color-shades-700",
    },
    PENDING_REVIEW: {
        id: STATUS_MODEL.PENDING_REVIEW,
        tittle : 'pending_review',
        toast: "pending_review",
        classCard: "card-shades-150-50",
        fontColor: "font-color-shades-700",
        infoColor: "icon-color-shades-700",
    },
    APPROVED: {
        id: STATUS_MODEL.APPROVED,
        tittle : 'approved',
        toast: "approved_by",
        classCard: "card-success-150-50",
        fontColor: "font-color-success-700",
        infoColor: "icon-color-success-700",
    },
    REJECTED: {
        id: STATUS_MODEL.REJECTED,
        tittle : 'rejected',
        toast: "rejected_by",
        classCard: "card-error-code-150-50",
        fontColor: "font-color-error-600",
        infoColor: "icon-color-error-code-700",
    },
    DENIED: {
        id: STATUS_MODEL.DENIED,
        tittle : 'denied',
        toast: "denied_by",
        classCard: "card-error-code-150-50",
        fontColor: "font-color-error-600",
        infoColor: "icon-color-error-code-700",
    },
    CANCELED: {
        id: STATUS_MODEL.CANCELED,
        tittle : 'canceled',
        toast: "canceled_by_admin",
        classCard: "card-error-code-150-50",
        fontColor: "font-color-error-600",
        infoColor: "icon-color-error-code-700",
    },
    CHANGES_REQUESTED: {
        id: STATUS_MODEL.CHANGES_REQUESTED,
        tittle : 'changes_requested',
        toast: "changes_requested_documentation",
        classCard: "card-warning-150-50",
        fontColor: "font-color-warning-700",
        infoColor: "icon-color-warning-700",
    },
    UNKNOWN: {
        id: -1,
        titleTrans: "unknown_status",
        displayTitle: "unknown_status",
        classCard: "card-shades-150-50",
        fontColor: "font-color-shades-700",
        infoColor: "icon-color-shades-700",
    },
};


export const INVITATION_REQUEST_DOCUMENTATION_DECISION_MODEL = {
    statusReason: null,
    option: '',
    invitationId: null,

    toRegister: function () {
        return {
            statusReason: this.statusReason,
            option: this.option,
            invitationId: this.invitationId,

        };
    },

    isValid: function () {
        return this.statusReason != null && this.statusReason.trim() !== "";
    }
};

/**
 * Determines whether biometric validation is enabled based on the user type.
 *
 * @param {Object} params the function parameters
 * @param {number} params.userTypeId the identifier of the user type
 * @param {Object} params.userRestrictions the biometric restriction configuration
 * @returns {boolean} true if biometric validation is enabled for the user type; otherwise false
 */
export const enableBiometricByUserTypeId = ({ userTypeId, userRestrictions}) =>{
    if(userTypeId === USER_TYPE_MODEL.VISITOR)
        return userRestrictions?.biometricVisitor;
    if(userTypeId === USER_TYPE_MODEL.EMPLOYEE)
        return userRestrictions?.biometricEmployee;
    if([USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE, USER_TYPE_MODEL.CONTRACTORS_EMPLOYEE].includes(userTypeId))
        return userRestrictions?.biometricContractor;
    if([USER_TYPE_MODEL.SUPPLIER_IN_CHARGE, USER_TYPE_MODEL.SUPPLIER_EMPLOYEE].includes(userTypeId))
        return userRestrictions?.biometricSupplier;

    return false;
}

/**
 * Determines whether identity validation is enabled based on the user type.
 *
 * @param {Object} params the function parameters
 * @param {number} params.userTypeId the identifier of the user type
 * @param {Object} params.userRestrictions the identity validation restriction configuration
 * @returns {boolean} true if identity validation is enabled for the user type; otherwise false
 */
export const enableValidationByUserTypeId = ({ userTypeId, userRestrictions}) =>{
    if(userTypeId === USER_TYPE_MODEL.VISITOR)
        return true;
    if(userTypeId === USER_TYPE_MODEL.EMPLOYEE)
        return userRestrictions?.validationEmployee;
    if([USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE, USER_TYPE_MODEL.CONTRACTORS_EMPLOYEE].includes(userTypeId))
        return userRestrictions?.validationContractor;
    if([USER_TYPE_MODEL.SUPPLIER_IN_CHARGE, USER_TYPE_MODEL.SUPPLIER_EMPLOYEE].includes(userTypeId))
        return userRestrictions?.validationSupplier;

    return false;
}

/**
 * Retrieves the update permission that governs the identity validation actions
 * for the origin the view was opened from.
 *
 * @param {number} userTypeId the identifier of the user type
 * @returns {number|null} the update permission id for the user type; otherwise null
 */
export const getUpdatePermissionByUserTypeId = (userTypeId) => {
    if(userTypeId === USER_TYPE_MODEL.VISITOR)
        return permissionObj?.WEB_VISITOR_UPDATE;
    if(userTypeId === USER_TYPE_MODEL.EMPLOYEE)
        return permissionObj?.WEB_EMPLOYEE_UPDATE;
    if([USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE, USER_TYPE_MODEL.CONTRACTORS_EMPLOYEE].includes(userTypeId))
        return permissionObj?.WEB_CONTRACTOR_UPDATE;
    if([USER_TYPE_MODEL.SUPPLIER_IN_CHARGE, USER_TYPE_MODEL.SUPPLIER_EMPLOYEE].includes(userTypeId))
        return permissionObj?.WEB_SUPPLIER_UPDATE;

    return null;
}

/**
 * Retrieves the translated documentation description according to the user type.
 *
 * @param {Object} params the function parameters
 * @param {number} params.userTypeId the identifier of the user type
 * @param {Object} params.identityValidationType the identity validation translation object
 * @returns {string|boolean} the translated description for the user type; otherwise false
 */
export const getDocumentationTransByUserTypeId = ({ userTypeId, identityValidationType }) =>{
    if(userTypeId === USER_TYPE_MODEL.VISITOR)
        return identityValidationType?.description;
    if([USER_TYPE_MODEL.EMPLOYEE, USER_TYPE_MODEL.CONTRACTORS_EMPLOYEE, USER_TYPE_MODEL.SUPPLIER_EMPLOYEE].includes(userTypeId))
        return identityValidationType?.descriptionEmployee;
    if(userTypeId === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE)
        return identityValidationType?.descriptionContractor;
    if(userTypeId === USER_TYPE_MODEL.SUPPLIER_IN_CHARGE)
        return identityValidationType?.descriptionSupplier;

    return false;
}

/**
 * Retrieves the UI configuration associated with a status identifier.
 *
 * Behavior:
 * - Searches all available status configurations defined in `StaticMsg`.
 * - Returns the matching configuration object for the provided status ID.
 * - Returns `StaticMsg.UNKNOWN` when no matching status configuration exists.
 *
 * Configuration includes:
 * - Translation keys
 * - Toast messages
 * - Card styles
 * - Font colors
 * - Icon colors
 *
 * @param {number} statusId - Status identifier to search.
 *
 * @returns {Object} Matching status configuration object
 * or the default unknown status configuration.
 */
export const getStatusConfig = (statusId) => {
    const allStatusConfigs = Object.values(StaticMsg);
    return allStatusConfigs.find(status => status.id === statusId) || StaticMsg.UNKNOWN;
};
